import React, { useState, useEffect, useCallback } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from './services/api';

/* Gestione delle categorie, riservata all'admin.
 *
 * Le categorie vivono su DUE livelli:
 *   1. Categoria        (Sole e mare, Cucina ed eventi, ...)
 *   2. Tipo di evento   (Lidi, Aperitivo, ...)
 * legati da un'associazione che porta anche l'ordine di visualizzazione.
 * Lo stesso tipo evento puo' stare sotto piu' categorie, quindi la pagina
 * mostra separatamente l'elenco completo dei tipi e quelli collegati.
 *
 * Il `code` non e' modificabile: e' la chiave con cui l'app traduce le
 * etichette, e cambiarlo le farebbe sparire silenziosamente.
 */
const GestioneCategorie = () => {
  const [albero, setAlbero] = useState([]);
  const [tipiEvento, setTipiEvento] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [espansa, setEspansa] = useState(null);

  const [nuovaCategoria, setNuovaCategoria] = useState({ code: '', descrizione: '', ordine: 0 });
  const [nuovoTipo, setNuovoTipo] = useState({ code: '', descrizione: '' });

  const carica = useCallback(async () => {
    setCaricamento(true);
    try {
      const [a, t] = await Promise.all([api.alberoTipologie(), api.tuttiTipiEvento()]);
      setAlbero(Array.isArray(a.data) ? a.data : []);
      setTipiEvento(Array.isArray(t.data) ? t.data : []);
    } catch (e) {
      toast.error('Impossibile caricare le categorie');
    } finally {
      setCaricamento(false);
    }
  }, []);

  useEffect(() => { carica(); }, [carica]);

  /* Gli errori del backend sono informativi (es. "collegata a 3 tipi evento"):
   * vanno mostrati come arrivano, non sostituiti da un generico. */
  const messaggioErrore = (e, ripiego) => e.response?.data?.message || ripiego;

  const creaCategoria = async () => {
    try {
      await api.creaCategoria(nuovaCategoria);
      toast.success('Categoria creata');
      setNuovaCategoria({ code: '', descrizione: '', ordine: 0 });
      carica();
    } catch (e) {
      toast.error(messaggioErrore(e, 'Creazione non riuscita'));
    }
  };

  const rinominaCategoria = async (cat) => {
    const descrizione = window.prompt('Nuovo nome della categoria:', cat.descrizione);
    if (descrizione === null) return;
    try {
      await api.aggiornaCategoria(cat.id, { descrizione });
      toast.success('Categoria aggiornata');
      carica();
    } catch (e) {
      toast.error(messaggioErrore(e, 'Aggiornamento non riuscito'));
    }
  };

  const cambiaOrdineCategoria = async (cat, delta) => {
    try {
      await api.aggiornaCategoria(cat.id, { ordine: (cat.ordine || 0) + delta });
      carica();
    } catch (e) {
      toast.error(messaggioErrore(e, 'Riordino non riuscito'));
    }
  };

  const eliminaCategoria = async (cat) => {
    if (!window.confirm(`Eliminare la categoria "${cat.descrizione}"?`)) return;
    try {
      await api.eliminaCategoria(cat.id);
      toast.success('Categoria eliminata');
      carica();
    } catch (e) {
      toast.error(messaggioErrore(e, 'Eliminazione non riuscita'));
    }
  };

  const creaTipo = async () => {
    try {
      await api.creaTipoEvento(nuovoTipo);
      toast.success('Tipo evento creato');
      setNuovoTipo({ code: '', descrizione: '' });
      carica();
    } catch (e) {
      toast.error(messaggioErrore(e, 'Creazione non riuscita'));
    }
  };

  const rinominaTipo = async (tipo) => {
    const descrizione = window.prompt('Nuovo nome del tipo evento:', tipo.descrizione);
    if (descrizione === null) return;
    try {
      await api.aggiornaTipoEvento(tipo.id, { descrizione });
      toast.success('Tipo evento aggiornato');
      carica();
    } catch (e) {
      toast.error(messaggioErrore(e, 'Aggiornamento non riuscito'));
    }
  };

  const eliminaTipo = async (tipo) => {
    if (!window.confirm(`Eliminare il tipo evento "${tipo.descrizione}"?`)) return;
    try {
      await api.eliminaTipoEvento(tipo.id);
      toast.success('Tipo evento eliminato');
      carica();
    } catch (e) {
      toast.error(messaggioErrore(e, 'Eliminazione non riuscita'));
    }
  };

  const associa = async (idCategoria, idTipo) => {
    try {
      await api.associaTipoEvento(idCategoria, idTipo, 0);
      toast.success('Tipo evento collegato');
      carica();
    } catch (e) {
      toast.error(messaggioErrore(e, 'Collegamento non riuscito'));
    }
  };

  const dissocia = async (idCategoria, idTipo) => {
    try {
      await api.dissociaTipoEvento(idCategoria, idTipo);
      toast.success('Tipo evento scollegato');
      carica();
    } catch (e) {
      toast.error(messaggioErrore(e, 'Scollegamento non riuscito'));
    }
  };

  const cambiaOrdineAssociazione = async (idCategoria, figlio, delta) => {
    try {
      await api.associaTipoEvento(idCategoria, figlio.id, (figlio.ordine || 0) + delta);
      carica();
    } catch (e) {
      toast.error(messaggioErrore(e, 'Riordino non riuscito'));
    }
  };

  if (caricamento) {
    return <div className="main-content"><p className="storico__vuoto">Caricamento categorie…</p></div>;
  }

  return (
    <div className="main-content">
      <div className="categorie">

        <header className="crea-evento__intestazione">
          <h1>Gestione categorie</h1>
          <p>Le categorie sono su due livelli: categoria e tipi di evento collegati.</p>
        </header>

        {/* ---------------- livello 1 ---------------- */}
        <section className="categorie__blocco">
          <h2>Categorie</h2>

          {albero.map((cat) => {
            const collegati = cat.tipologieEvento || [];
            const idCollegati = collegati.map((f) => f.id);
            const disponibili = tipiEvento.filter((t) => !idCollegati.includes(t.id));
            const aperta = espansa === cat.id;

            return (
              <article key={cat.id} className="categorie__card">
                <div className="categorie__riga">
                  <div>
                    <strong>{cat.descrizione}</strong>
                    <span className="categorie__code">{cat.code}</span>
                    <span className="categorie__conteggio">
                      {collegati.length} {collegati.length === 1 ? 'tipo' : 'tipi'}
                    </span>
                  </div>
                  <div className="categorie__azioni">
                    <button type="button" onClick={() => cambiaOrdineCategoria(cat, -1)} title="Sposta su">↑</button>
                    <button type="button" onClick={() => cambiaOrdineCategoria(cat, 1)} title="Sposta giù">↓</button>
                    <button type="button" onClick={() => rinominaCategoria(cat)}>Rinomina</button>
                    <button type="button" onClick={() => eliminaCategoria(cat)}>Elimina</button>
                    <button type="button" onClick={() => setEspansa(aperta ? null : cat.id)}>
                      {aperta ? 'Chiudi' : 'Tipi evento'}
                    </button>
                  </div>
                </div>

                {aperta && (
                  <div className="categorie__dettaglio">
                    <h4>Tipi evento collegati</h4>
                    {collegati.length === 0 ? (
                      <p className="scheda-dati__nota">Nessun tipo evento collegato.</p>
                    ) : (
                      <ul className="categorie__elenco">
                        {collegati.map((f) => (
                          <li key={f.id}>
                            <span>{f.descrizione} <em>{f.code}</em></span>
                            <span className="categorie__azioni">
                              <button type="button" onClick={() => cambiaOrdineAssociazione(cat.id, f, -1)}>↑</button>
                              <button type="button" onClick={() => cambiaOrdineAssociazione(cat.id, f, 1)}>↓</button>
                              <button type="button" onClick={() => dissocia(cat.id, f.id)}>Scollega</button>
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}

                    <h4>Collega un tipo evento</h4>
                    {disponibili.length === 0 ? (
                      <p className="scheda-dati__nota">Tutti i tipi evento sono già collegati.</p>
                    ) : (
                      <div className="pannello-admin__riga">
                        <select
                          defaultValue=""
                          onChange={(e) => { if (e.target.value) associa(cat.id, Number(e.target.value)); }}
                          aria-label="Tipo evento da collegare"
                        >
                          <option value="">Seleziona…</option>
                          {disponibili.map((t) => (
                            <option key={t.id} value={t.id}>{t.descrizione}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}
              </article>
            );
          })}

          <div className="categorie__nuovo">
            <h4>Nuova categoria</h4>
            <div className="pannello-admin__riga">
              <input
                placeholder="code (minuscolo, es. sport)"
                value={nuovaCategoria.code}
                onChange={(e) => setNuovaCategoria({ ...nuovaCategoria, code: e.target.value })}
              />
              <input
                placeholder="Nome visibile"
                value={nuovaCategoria.descrizione}
                onChange={(e) => setNuovaCategoria({ ...nuovaCategoria, descrizione: e.target.value })}
              />
              <button type="button" onClick={creaCategoria}
                      disabled={!nuovaCategoria.code || !nuovaCategoria.descrizione}>
                Crea
              </button>
            </div>
            <p className="scheda-dati__nota">
              Il code non sarà più modificabile: è la chiave usata dall’app per tradurre l’etichetta.
            </p>
          </div>
        </section>

        {/* ---------------- livello 2 ---------------- */}
        <section className="categorie__blocco">
          <h2>Tipi di evento</h2>

          <div className="tabella-scroll">
            <table>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Code</th>
                  <th>In categorie</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {tipiEvento.map((t) => (
                  <tr key={t.id}>
                    <td>{t.descrizione}</td>
                    <td><code>{t.code}</code></td>
                    <td>{t.usatoInCategorie}</td>
                    <td>
                      <button type="button" onClick={() => rinominaTipo(t)}>Rinomina</button>{' '}
                      <button type="button" onClick={() => eliminaTipo(t)}>Elimina</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="categorie__nuovo">
            <h4>Nuovo tipo di evento</h4>
            <div className="pannello-admin__riga">
              <input
                placeholder="code (minuscolo)"
                value={nuovoTipo.code}
                onChange={(e) => setNuovoTipo({ ...nuovoTipo, code: e.target.value })}
              />
              <input
                placeholder="Nome visibile"
                value={nuovoTipo.descrizione}
                onChange={(e) => setNuovoTipo({ ...nuovoTipo, descrizione: e.target.value })}
              />
              <button type="button" onClick={creaTipo}
                      disabled={!nuovoTipo.code || !nuovoTipo.descrizione}>
                Crea
              </button>
            </div>
            <p className="scheda-dati__nota">
              Un tipo appena creato non compare finché non lo colleghi a una categoria.
            </p>
          </div>
        </section>

      </div>
      <ToastContainer />
    </div>
  );
};

export default GestioneCategorie;
